import React, { useEffect, useState, useCallback } from 'react';

export interface Connection {
    from: HTMLElement | null;
    to: HTMLElement | null;
    dashed?: boolean;
}

interface MdlConnectorsProps {
    containerRef: React.RefObject<HTMLDivElement>;
    connections: Connection[];
    /** muda quando algo que afeta posições muda (ex.: dados carregados) */
    revision?: unknown;
}

interface Path {
    d: string;
    dashed: boolean;
}

// Overlay SVG que desenha curvas bézier horizontais entre a borda direita do
// elemento `from` e a borda esquerda do elemento `to`, em coordenadas relativas
// ao container. Recalcula em resize/scroll e quando `revision` muda.
const MdlConnectors: React.FC<MdlConnectorsProps> = ({ containerRef, connections, revision }) => {
    const [paths, setPaths] = useState<Path[]>([]);
    const [size, setSize] = useState({ w: 0, h: 0 });

    const recompute = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;
        const base = container.getBoundingClientRect();
        setSize({ w: base.width, h: base.height });

        const next: Path[] = [];
        connections.forEach(({ from, to, dashed }) => {
            if (!from || !to) return;
            const a = from.getBoundingClientRect();
            const b = to.getBoundingClientRect();
            const x1 = a.right - base.left;
            const y1 = a.top - base.top + a.height / 2;
            const x2 = b.left - base.left;
            const y2 = b.top - base.top + b.height / 2;
            const dx = Math.max(40, Math.abs(x2 - x1) * 0.5);
            next.push({
                d: `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`,
                dashed: Boolean(dashed),
            });
        });
        setPaths(next);
    }, [containerRef, connections]);

    useEffect(() => {
        recompute();
        const id = window.setTimeout(recompute, 60); // após layout/paint
        window.addEventListener('resize', recompute);
        const container = containerRef.current;
        container?.addEventListener('scroll', recompute, true);
        return () => {
            window.clearTimeout(id);
            window.removeEventListener('resize', recompute);
            container?.removeEventListener('scroll', recompute, true);
        };
    }, [recompute, revision]);

    return (
        <svg
            className="absolute inset-0 pointer-events-none"
            width={size.w}
            height={size.h}
            style={{ zIndex: 1 }}
        >
            {paths.map((p, i) => (
                <path
                    key={i}
                    d={p.d}
                    fill="none"
                    stroke="#f97316"
                    strokeWidth={1.5}
                    strokeDasharray={p.dashed ? '5 4' : undefined}
                    markerEnd="url(#mdl-arrow)"
                />
            ))}
            <defs>
                <marker id="mdl-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill="#f97316" />
                </marker>
            </defs>
        </svg>
    );
};

export default MdlConnectors;
